import { Box, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { useMe } from '@/api';
import { useSession } from '@/auth';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { DEV_TOOLS_ENABLED } from '@/config';
import { LANGUAGES, isLanguage, setLanguage } from '@/i18n';
import { setSampleProjectEnabled, useSampleProjectEnabled } from '@/lib';
import { layout } from '@/theme';

/**
 * The 250px navigation rail, following
 * Web/src/components/global/Sidebar/components/SidebarContent/.
 *
 * The identity block — name, company, then Logout — is pinned to the bottom with
 * `margin-top: auto`, which is where TimeLog puts it. It is deliberately *not* in
 * the top bar: `Web/src/components/global/TopNavigation/TopNavigation.tsx` has no
 * username, avatar or profile element at all.
 *
 * The rail's own `padding-left: 20px` is what produces the page's left gutter, so
 * `AppShell` adds no left padding of its own.
 *
 * Top-level destinations ONLY. A project's areas (Planning, Financials, Team,
 * Billing) used to sit here under a "Project" group — duplicated once per id
 * space — but in TimeLog this rail is the product's own navigation and has no
 * room for per-project sub-navigation. They live in the project header now
 * (`components/project/ProjectAreaNav`); see ADR 0031.
 */

/** Shared row geometry for every sidebar entry, incl. the profile row. */
const rowSx = {
  display: 'flex',
  alignItems: 'center',
  columnGap: 1,
  px: 1.5,
  py: 0.75,
  borderRadius: 'var(--border-radius-medium)',
  color: 'var(--fg-1)',
  textDecoration: 'none',
  transition: 'background-color var(--duration-instant) ease-in',
  '&:hover': { bgcolor: 'var(--bg-hover)' },
  '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '-2px' },
} as const;

const NavRow = ({
  to,
  icon,
  label,
  active,
  automationId,
}: {
  to: string;
  icon: string;
  label: string;
  active?: boolean;
  automationId?: string;
}) => (
  <Box
    component={NavLink}
    to={to}
    end
    data-automation-id={automationId}
    aria-current={active ? 'page' : undefined}
    sx={{
      ...rowSx,
      ...(active ? { bgcolor: 'var(--bg-active)', fontWeight: 500 } : {}),
      '&:hover': active ? { bgcolor: 'var(--bg-active)' } : rowSx['&:hover'],
    }}
  >
    <MaterialSymbol name={icon} size={20} />
    <Typography variant="bodyRegular" component="span">
      {label}
    </Typography>
  </Box>
);

/**
 * The one developer control in the product's navigation, and it earns its place
 * by removing a thing to remember: the sample-project button is off by default
 * (ADR 0039), and turning it back on otherwise means restarting the dev server
 * with `VITE_SAMPLE_PROJECT=on` because Vite inlines env vars at boot.
 *
 * Deliberately quiet — muted text, no icon badge, sat apart under the nav —
 * because it is scaffolding, not a feature. And deliberately dev-only:
 * `DEV_TOOLS_ENABLED` is `import.meta.env.DEV`, a compile-time constant, so a
 * production build folds this away and no deployed environment can show it.
 */
const SampleDataToggle = () => {
  const enabled = useSampleProjectEnabled();
  const { t } = useTranslation();

  return (
    <Box
      component="button"
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => setSampleProjectEnabled(!enabled)}
      data-automation-id="sample-data-toggle"
      title={enabled ? t('sampleData.HideTitle') : t('sampleData.ShowTitle')}
      sx={{
        ...rowSx,
        justifyContent: 'space-between',
        width: '100%',
        border: 'none',
        bgcolor: 'transparent',
        cursor: 'pointer',
        color: 'var(--fg-3)',
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--font-size-label)',
      }}
    >
      {t('sampleData.Label')}
      <MaterialSymbol
        name={enabled ? 'toggle_on' : 'toggle_off'}
        size={20}
        color={enabled ? 'var(--color-pink-80)' : 'var(--fg-3)'}
      />
    </Box>
  );
};

/**
 * Dansk / English (ADR 0049). A row like the others in the identity block, its
 * menu opening upward from the rail's foot. Each language is named in itself,
 * so someone who cannot read the current one still finds their own.
 */
const LanguagePicker = () => {
  const { t, i18n } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = isLanguage(i18n.language) ? i18n.language : 'en';

  return (
    <>
      <Box
        component="button"
        type="button"
        data-automation-id="language-picker"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label={t('language.PickerLabel')}
        onClick={(event: React.MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
        sx={{
          ...rowSx,
          width: '100%',
          border: 'none',
          bgcolor: 'transparent',
          font: 'inherit',
          fontFamily: 'var(--font-family)',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <MaterialSymbol name="language" size={20} />
        <Typography variant="bodyRegular" component="span" sx={{ flex: 1 }}>
          {t(`language.${current}`)}
        </Typography>
        <MaterialSymbol name="unfold_more" size={16} color="var(--fg-3)" />
      </Box>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {LANGUAGES.map((language) => (
          <MenuItem
            key={language}
            lang={language}
            selected={language === current}
            data-automation-id="language-option"
            data-language={language}
            onClick={() => {
              setLanguage(language);
              setAnchor(null);
            }}
          >
            {t(`language.${language}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export type SidebarProps = {
  /** Collapsed rails render nothing; `AppShell` widens the page instead. */
  collapsed?: boolean;
};

export const Sidebar = ({ collapsed = false }: SidebarProps) => {
  const session = useSession();
  // The company line comes from the API, not the token — `SessionUser` carries
  // only displayName and subject.
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const { t } = useTranslation(['pmShell', 'navigation']);

  if (collapsed) return null;

  return (
    <Stack
      component="nav"
      aria-label={t('nav.MainLabel')}
      gap={0}
      data-automation-id="sidebar"
      sx={{
        minWidth: layout.sidebarWidth,
        maxWidth: layout.sidebarWidth,
        height: 'var(--page-wrapper-height)',
        // 20px left is the page's left gutter; 12px right clears the scrollbar.
        pt: '22px',
        pr: 1.5,
        pb: 2,
        pl: '20px',
      }}
    >
      <Stack sx={{ flexDirection: 'column', height: '100%' }}>
        <Stack gap={2} sx={{ overflowY: 'auto', p: '2px' }}>
          {/*
            Projects only. The Resource Planner is deliberately NOT in the menu:
            this prototype is about planning and scoping a project, and a
            tenant-wide utilisation grid is a different product surface that
            invited reviewers to wander off into it.

            The page and its `/resources` route are untouched and still work by
            direct URL — the planner suite and its screenshots navigate that way.
            Restoring the row is a one-liner if the scope changes.
          */}
          <Stack gap={0.25}>
            <NavRow
              to="/"
              icon="view_object_track"
              label={t('navigation:ProjectsParent')}
              active={pathname === '/'}
              automationId="nav-projects"
            />
          </Stack>

          {DEV_TOOLS_ENABLED ? <SampleDataToggle /> : null}
        </Stack>

        {/* margin-top: auto is what pins identity to the bottom of the rail. */}
        <Stack gap={0.75} sx={{ mt: 'auto', flexShrink: 0, p: '2px', mr: '11px' }}>
          <Box sx={{ ...rowSx, cursor: 'default', '&:hover': {} }}>
            <MaterialSymbol name="account_circle" size={20} color="var(--fg-3)" />
            <Stack gap={0} sx={{ minWidth: 0 }}>
              <Typography variant="bodyBold" component="span" data-automation-id="current-user">
                {session.user?.displayName ?? '—'}
              </Typography>
              {/*
                Server-derived: `siteName` comes from /api/me, which resolves it
                from the bearer token. It is the one thing in this block the
                client could not have made up, which is why the auth e2e reads
                it — the POC identity card that used to say so is gone.
              */}
              {me?.auth.siteName ? (
                <Typography
                  variant="label"
                  component="span"
                  sx={{ color: 'var(--fg-2)' }}
                  data-automation-id="current-site"
                >
                  {me.auth.siteName}
                </Typography>
              ) : null}
            </Stack>
          </Box>
          <LanguagePicker />
          <Box
            component="button"
            type="button"
            onClick={session.signOut}
            data-automation-id="signout-button"
            sx={{
              ...rowSx,
              width: '100%',
              border: 'none',
              bgcolor: 'transparent',
              font: 'inherit',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <MaterialSymbol name="logout" size={20} />
            <Typography variant="bodyRegular" component="span">
              {t('navigation:ProfileLogout')}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};
